import React, { useState, useEffect, createContext } from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import CustomerDashboard from './CustomerDashboard';  // Main dashboard
import CustomerList from './CustomerList';            // Customer list page
import CustomerProfile from './CustomerProfile';      // Individual customer profile
import Login from './Login';
import PrivateRoute from './PrivateRoute';
import logo from './midflorida-logo.svg';
import { authFetch, logout, fetchCsrfToken } from './authFetch';
import AdminImports from './AdminImports';
import AdminPDFTemplates from './AdminPDFTemplates';  // PDF Template management
import CalendarView from './CalendarView';  // Calendar component
import MyProfile from './MyProfile';        // Agent profile page
import HistoricalFiles from './HistoricalFiles';  // Historical client files browser
import CallRecordings from './CallRecordings';    // Call recordings browser (admin-only)
import { Calendar, User, Archive, Phone } from 'lucide-react';    // Icons
import IdleTimeout from './IdleTimeout';    // HIPAA-compliant idle timeout
import PublicFormSigning from './PublicFormSigning'; // Public signing page (no auth)

// OAuth callback component
import OAuthCallback from './OAuthCallback';
import Debug from './Debug';  // Debug component for troubleshooting

// ===== DEBUG MODE TOGGLE =====
// Set to true to enable debug route for troubleshooting
// Set to false for production (default)
const DEBUG_MODE = false;
// =============================

export const UserContext = createContext(null);

// Wrapper component for debug route
function DebugRoute() {
  if (!DEBUG_MODE) {
    return <Navigate to="/" />;
  }
  
  return (
    <div className="max-w-4xl mx-auto mt-8 p-8">
      <div className="bg-yellow-50 border border-yellow-300 rounded p-4 mb-4">
        <h2 className="text-xl font-bold text-yellow-800 mb-2">🔧 Debug Mode</h2>
        <p className="text-sm text-gray-700">
          Debug tools are active. All authentication details are also logged to the browser console (F12).
        </p>
      </div>
      <Debug />
    </div>
  );
}

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = currentUser?.is_staff || (currentUser?.groups || []).some(g => (g.name || '').toLowerCase() === 'admin');

  useEffect(() => {
    const checkUser = async () => {
      try {
        const BASE_URL = process.env.REACT_APP_API_BASE_URL;
        await fetchCsrfToken();
        const response = await authFetch(`${BASE_URL}/api/auth/user/`);
        if (response.ok) {
          const userData = await response.json();
          const userDetailResponse = await authFetch(`${BASE_URL}/api/auth/debug/`);
          if (userDetailResponse.ok) {
            const userDetails = await userDetailResponse.json();
            const enrichedUserData = {
              ...userData,
              id: userDetails.id,
              groups: userDetails.groups,
              is_staff: userDetails.is_staff,
              is_superuser: userDetails.is_superuser
            };
            console.log("Enriched user data:", enrichedUserData);
            localStorage.setItem('userDetails', JSON.stringify(enrichedUserData));
            setCurrentUser(enrichedUserData);
          } else {
            setCurrentUser(userData);
          }
        }
      } catch (error) {
        console.error("Error checking user:", error);
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      setCurrentUser(null);
      localStorage.removeItem('userDetails');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <UserContext.Provider value={{ currentUser, setCurrentUser }}>
      <IdleTimeout isAuthenticated={!!currentUser}>
        <div className="App min-h-screen bg-gray-100">
          {/* Header with calendar navigation */}
          <header className="bg-blue-800 text-white p-4 relative flex justify-center items-center">
            <Link to="/">
              <img src={logo} alt="Mid Florida Agencies Logo" style={{ height: '85px' }} />
            </Link>

            {/* LEFT SIDE: Admin and Calendar Links */}
            {currentUser && (
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex space-x-3">
                {/* Calendar Link - Available to all authenticated users */}
                <Link
                  to="/calendar"
                  className="flex items-center text-white bg-blue-600 px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Calendar
                </Link>

                {/* Historical Files Link - Available to all authenticated users */}
                <Link
                  to="/historical-files"
                  className="flex items-center text-white bg-amber-600 px-3 py-1 rounded hover:bg-amber-700 transition-colors"
                >
                  <Archive className="h-4 w-4 mr-1" />
                  Historical Files
                </Link>

                {/* Admin Links - Only for admins */}
                {isAdmin && (
                  <>
                    <Link
                      to="/admin/imports"
                      className="text-white bg-green-600 px-3 py-1 rounded hover:bg-green-700 transition-colors"
                    >
                      Admin Imports
                    </Link>
                    <Link
                      to="/admin/pdf-templates"
                      className="text-white bg-purple-600 px-3 py-1 rounded hover:bg-purple-700 transition-colors"
                    >
                      PDF Templates
                    </Link>
                    <Link
                      to="/admin/call-recordings"
                      className="flex items-center text-white bg-red-600 px-3 py-1 rounded hover:bg-red-700 transition-colors"
                    >
                      <Phone className="h-4 w-4 mr-1" />
                      Recordings
                    </Link>
                  </>
                )}
              </div>
            )}

            {/* RIGHT SIDE: Profile and Logout */}
            {currentUser && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex space-x-3">
                <Link
                  to="/profile"
                  className="flex items-center text-white bg-blue-600 px-3 py-1 rounded hover:bg-blue-700 transition-colors"
                >
                  <User className="h-4 w-4 mr-1" />
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-white bg-red-600 px-4 py-1 rounded hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </header>

          <main className="container mx-auto p-4">
            <Routes>
              <Route path="/login" element={<Login />} />

              {/* OAuth callback route */}
              <Route path="/oauth/success" element={<OAuthCallback />} />

              {/* Main Dashboard Route */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <CustomerDashboard />
                  </PrivateRoute>
                }
              />

              {/* Customer List Route */}
              <Route
                path="/customers"
                element={
                  <PrivateRoute>
                    <CustomerList />
                  </PrivateRoute>
                }
              />

              {/* Individual Customer Profile Route */}
              <Route
                path="/customers/:id"
                element={
                  <PrivateRoute>
                    <CustomerProfile />
                  </PrivateRoute>
                }
              />

              {/* Calendar Route */}
              <Route
                path="/calendar"
                element={
                  <PrivateRoute>
                    <CalendarView />
                  </PrivateRoute>
                }
              />

              {/* Profile Route */}
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <MyProfile />
                  </PrivateRoute>
                }
              />

              {/* Historical Files Route */}
              <Route
                path="/historical-files"
                element={
                  <PrivateRoute>
                    <HistoricalFiles />
                  </PrivateRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/imports"
                element={
                  <PrivateRoute>
                    <AdminImports />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/pdf-templates"
                element={
                  <PrivateRoute>
                    <AdminPDFTemplates />
                  </PrivateRoute>
                }
              />

              {/* Call Recordings Route (Admin-only) */}
              <Route
                path="/admin/call-recordings"
                element={
                  <PrivateRoute>
                    <CallRecordings />
                  </PrivateRoute>
                }
              />

              {/* Public Form Signing Route (no auth required) */}
              <Route path="/sign/:token" element={<PublicFormSigning />} />

              {/* Debug Route - Only accessible when DEBUG_MODE = true */}
              <Route
                path="/debug"
                element={
                  <PrivateRoute>
                    <DebugRoute />
                  </PrivateRoute>
                }
              />

              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>
        </div>
      </IdleTimeout>
    </UserContext.Provider>
  );
}

export default App;
